import { DtoBase } from "../Utils/DtoBase";
import { HashSet } from "../Utils/HashSet";
import { List } from "../Utils/List";
import { IpScope } from "../Core/IpScope";
import { DeviceCategory } from "../Core/DeviceCategory";
import { ValueEncoder } from "../Utils/ValueEncoder";
import { ObjectSerializer } from "../Serialization/ObjectSerializer";
import { ObjSerUtils } from "../Serialization/ObjSerUtils";
import { ObjUtils, Iterable, IComparable } from "../Utils/ObjUtils";
import { StringUtils } from "../Utils/StringUtils";
import { Device } from "../Core/Device";
import { SystemInfo } from "../InfoSets/SystemInfo";

export enum IncludeScope {
	NoDevices,
	AllDevices,
	AllNetworkDevices,
	AllSnmpDevices
}

export enum CompositeFilterType {
	All,
	Any
};

export class DeviceFilter extends DtoBase {

	public static IncludeScope = IncludeScope;
	public static CompositeFilterType = CompositeFilterType;

	constructor() {
		super();

		this._nameFilters = new HashSet<string>();
		this._ipScopeFilters = new HashSet<string>();
		this._categoryFilters = new HashSet<string>();
		this._vlanFilters = new HashSet<string>();
		this._oidFilters = new HashSet<string>();
		this._portFilters = new HashSet<string>();
		this._customFilter = null;

		this.Clear();
	}

	public Clear() {
		this.Name = '';

		this._baseIncludeScope = IncludeScope.AllDevices;
		this._categoryFilterType = CompositeFilterType.Any;

		this.ClearNameFilters();
		this.ClearIpScopeFilters();
		this.ClearCategoryFilters();
		this.ClearVlanFilters();
		this.ClearOidFilters();
		this.ClearCustomFilter();

		this.ClearRawDeviceFilter();
	}

	public Copy(other: DeviceFilter) {
		this.Name = other.Name;

		this._baseIncludeScope = other._baseIncludeScope;
		this._categoryFilterType = other._categoryFilterType;

		this._nameFilters = new HashSet<string>(other._nameFilters);
		this._ipScopeFilters = new HashSet<string>(other._ipScopeFilters);
		this._categoryFilters = new HashSet<string>(other._categoryFilters);
		this._vlanFilters = new HashSet<string>(other._vlanFilters);
		this._oidFilters = new HashSet<string>(other._oidFilters);
		this._portFilters = new HashSet<string>(other._portFilters);

		// TODO - Copy custom filter

		this.ClearRawDeviceFilter();
	}


	private _name: string;

	public get Name(): string {
		return this._name;
	}

	public set Name(val: string) {
		this._name = val;
	}

	public get HasName(): boolean {
		return !!this._name;
	}


	private _rawFilterTree: IDeviceFilter;


	private _baseIncludeScope: IncludeScope;

	public get BaseIncludeScope(): IncludeScope {
		return this._baseIncludeScope;
	}

	public set BaseIncludeScope(val: IncludeScope) {
		this._baseIncludeScope = val;
		this.ClearRawDeviceFilter();
	}


	private _nameFilters: HashSet<string>;

	public get HasNameFilters(): boolean {
		return this._nameFilters && this._nameFilters.Count > 0;
	}

	public get NameFilters(): List<string> {
		return this._nameFilters.ToList();
	}

	public AddNameFilter(nameFilter: string): boolean {
		if (!nameFilter)
			return false;

		var newFilter = nameFilter.trim();

		if (!newFilter)
			return false;

		if (this._nameFilters.Contains(newFilter))
			return true;

		this._nameFilters.Add(newFilter);

		this.ClearRawDeviceFilter();
		return true;
	}

	public ClearNameFilters() {
		if (this._nameFilters.Count > 0) {
			this._nameFilters.Clear();
			this.ClearRawDeviceFilter();
		}
	}

	public RemoveNameFilter(nameFilter: string) {
		if (this._nameFilters.Contains(nameFilter)) {
			this._nameFilters.Remove(nameFilter);
			this.ClearRawDeviceFilter();
		}
	}


	private _ipScopeFilters: HashSet<string>;

	public get HasIpScopeFilters(): boolean {
		return this._ipScopeFilters && this._ipScopeFilters.Count > 0;
	}

	public get IpScopeFilters(): List<string> {
		return this._ipScopeFilters.ToList();
	}

	public AddIpScopeFilter(scopeFilter: string): boolean {
		if (!scopeFilter)
			return false;

		var newFilter = scopeFilter.trim();

		if (!newFilter)
			return false;

		// TRY to PARSE/ADD new filter 
		var ipScope = new IpScope();
		ipScope.Add(newFilter);
		if (ipScope.IsEmpty)
			return false;

		this._ipScopeFilters.Add(newFilter);

		this.ClearRawDeviceFilter();
		return true;
	}

	public ClearIpScopeFilters() {
		if (this._ipScopeFilters.Count > 0) {
			this._ipScopeFilters.Clear();
			this.ClearRawDeviceFilter();
		}
	}

	public RemoveIpScopeFilter(scopeFilter: string) {
		if (this._ipScopeFilters.Contains(scopeFilter)) {
			this._ipScopeFilters.Remove(scopeFilter);
			this.ClearRawDeviceFilter();
		}
	}


	private _categoryFilterType: CompositeFilterType;
	public get CategoryFilterType(): CompositeFilterType {
		return this._categoryFilterType;
	}

	public set CategoryFilterType(value: CompositeFilterType) {
		this._categoryFilterType = value;
		this.ClearRawDeviceFilter();
	}


	private _categoryFilters: HashSet<string>;
	public get HasCategoryFilters(): boolean {
		if (this._categoryFilters == null)
			return false;
		if (this._categoryFilters.Count <= 0)
			return false;
		return true;
	}

	public get CategoryFilters(): List<string> {
		return this._categoryFilters.ToList();
	}

	public AddCategoryFilterByName(categoryName: string): boolean {
		if (!categoryName)
			return false;

		var newFilter = categoryName.trim();

		if (!newFilter)
			return false;

		if (this._categoryFilters.Contains(newFilter))
			return true;

		var devCategory = DeviceCategory.Get(newFilter);
		if (devCategory == null)
			return false;

		this._categoryFilters.Add(newFilter);

		this.ClearRawDeviceFilter();
		return true;
	}

	public AddCategoryFilter(category: DeviceCategory): boolean {
		return (this.AddCategoryFilterByName(category.Name));
	}

	public ClearCategoryFilters() {
		if (this._categoryFilters.Count > 0) {
			this._categoryFilters.Clear();
			this.ClearRawDeviceFilter();
		}
	}

	public ContainsCategoryFilter(categoryName: string): boolean {
		if (this._categoryFilters == null)
			return false;
		return (this._categoryFilters.Contains(categoryName));
	}

	public RemoveCategoryFilter(categoryName: string) {
		if (this._categoryFilters.Contains(categoryName)) {
			this._categoryFilters.Remove(categoryName);
			this.ClearRawDeviceFilter();
		}
	}


	private _vlanFilters: HashSet<string>;
	public get HasVlanFilters(): boolean {
		if (this._vlanFilters == null)
			return false;
		if (this._vlanFilters.Count <= 0)
			return false;
		return true;
	}

	public get VlanFilters(): List<string> {
		return this._vlanFilters.ToList();
	}

	public AddVlanFilter(vlanName: string): boolean {
		if (!vlanName)
			return false;

		var newFilter = vlanName.trim();

		if (!newFilter)
			return false;

		if (this._vlanFilters.Contains(newFilter))
			return true;

		this._vlanFilters.Add(newFilter);

		this.ClearRawDeviceFilter();
		return true;
	}

	public ClearVlanFilters() {
		if (this._vlanFilters.Count > 0) {
			this._vlanFilters.Clear();
			this.ClearRawDeviceFilter();
		}
	}

	public RemoveVlanFilter(vlanName: string) {
		if (this._vlanFilters.Contains(vlanName)) {
			this._vlanFilters.Remove(vlanName);
			this.ClearRawDeviceFilter();
		}
	}


	private _oidFilters: HashSet<string>;
	public get HasOidFilters(): boolean {
		if (this._oidFilters == null)
			return false;
		if (this._oidFilters.Count <= 0)
			return false;
		return true;
	}

	public get OidFilters(): List<string> {
		return this._oidFilters.ToList();
	}

	public AddOidFilter(oidFilter: string): boolean {
		if (!oidFilter)
			return false;

		var newFilter = oidFilter.trim();

		if (!newFilter)
			return false;

		if (this._oidFilters.Contains(newFilter))
			return true;

		this._oidFilters.Add(newFilter);

		this.ClearRawDeviceFilter();
		return true;
	}

	public ClearOidFilters() {
		if (this._oidFilters.Count > 0) {
			this._oidFilters.Clear();
			this.ClearRawDeviceFilter();
		}
	}

	public RemoveOidFilter(oidFilter: string) {
		if (this._oidFilters.Contains(oidFilter)) {
			this._oidFilters.Remove(oidFilter);
			this.ClearRawDeviceFilter();
		}
	}

	private _portFilters: HashSet<string>;
	public get HasPortFilters(): boolean {
		if (this._portFilters == null)
			return false;
		if (this._portFilters.Count <= 0)
			return false;
		return true;
	}

	public get PortFilters(): List<string> {
		return this._portFilters.ToList();
	}

	public AddPortFilter(portFilter: string): boolean {
		if (!portFilter)
			return false;

		var newFilter = portFilter.trim();
		if (!newFilter)
			return false;

		let portAdded = false;

		const filterList = newFilter.split(/[,;]/)
		if (filterList) {
			filterList.forEach(port => {
				if (!this._portFilters.Contains(port)) {
					this._portFilters.Add(port);
					portAdded = true;
				}
			});
		}

		if (portAdded) {
			this.ClearRawDeviceFilter();
		}
		return portAdded;
	}

	public ClearPortFilters() {
		if (this._portFilters.Count > 0) {
			this._portFilters.Clear();
			this.ClearRawDeviceFilter();
		}
	}

	public RemovePortFilter(portFilter: string) {
		if (this._portFilters.Contains(portFilter)) {
			this._portFilters.Remove(portFilter);
			this.ClearRawDeviceFilter();
		}
	}

	private _customFilter: IDeviceFilter;
	public get CustomFilter(): IDeviceFilter {
		return this._customFilter;
	}

	public set CustomFilter(val: IDeviceFilter) {
		this._customFilter = val;
		this.ClearRawDeviceFilter();
	}

	public ClearCustomFilter() {
		this._customFilter = null;
		this.ClearRawDeviceFilter();
	}

	public get HasCustomFilter(): boolean {
		if (this._customFilter)
			return true;
		else
			return false;
	}

	private BuildRawDeviceFilter(): IDeviceFilter {
		if (this.HasCustomFilter) {
			return this.CustomFilter;
		}
		else {
			var rootAndFilter = new AndFilter();

			// BASE DEVICE Filter
			var deviceFilter = new AndFilter();
			switch (this.BaseIncludeScope) {
				case IncludeScope.AllDevices:
					// Do Nothing
					break;

				case IncludeScope.AllSnmpDevices:
					deviceFilter.Add(new DeviceCategoryFilter(DeviceCategory.Snmp));
					break;

				case IncludeScope.AllNetworkDevices:
					deviceFilter.Add(new DeviceCategoryFilter(DeviceCategory.NetworkDevice));
					break;

				case IncludeScope.NoDevices:
					// Causes no devices to match the overall filter
					deviceFilter.Add(new OrFilter());
					break;

				default:
					break;
			}
			rootAndFilter.Add(deviceFilter);

			// NAME Filter
			if (this.HasNameFilters) {
				var nameFilter = new OrFilter();
				this.NameFilters.ForEach((name) => {
					nameFilter.Add(new DeviceNameFilter(name));
				});

				rootAndFilter.Add(nameFilter);
			}

			if (this.HasIpScopeFilters) {
				var ipScopeFilter = new OrFilter();

				var ipScope = new IpScope();
				this.IpScopeFilters.ForEach((ipScopeStr) => {
					ipScope.Add(ipScopeStr);
				});

				if (!ipScope.IsEmpty) {
					ipScopeFilter.Add(new IpScopeFilter(ipScope));
					rootAndFilter.Add(ipScopeFilter);
				}
			}

			if (this.HasCategoryFilters) {
				var categoryFilter: ICompositeDeviceFilter = null;
				switch (this._categoryFilterType) {
					case CompositeFilterType.Any:
						categoryFilter = new OrFilter();
						break;
					case CompositeFilterType.All:
						categoryFilter = new AndFilter();
						break;
					default:
						break;
				}

				var deviceCategory: DeviceCategory = null;

				this.CategoryFilters.ForEach((categoryName) => {
					deviceCategory = DeviceCategory.Get(categoryName);
					if (!deviceCategory)
						return;
					categoryFilter.Add(new DeviceCategoryFilter(deviceCategory));
				});

				rootAndFilter.Add(categoryFilter);
			}

			if (this.HasVlanFilters) {
				var vlanFilter = new OrFilter();
				this.VlanFilters.ForEach((vlanName) => {
					vlanFilter.Add(new VlanFilter(vlanName));
				});
				rootAndFilter.Add(vlanFilter);
			}

			if (this.HasOidFilters) {
				var oidFilter = new OrFilter();
				this.OidFilters.ForEach((oidStr) => {
					oidFilter.Add(new OidFilter(oidStr));
				});
				rootAndFilter.Add(oidFilter);
			}

			if (this.HasPortFilters) {
				var portFilter = new OrFilter();
				this.PortFilters.ForEach((portStr) => {
					portFilter.Add(new PortFilter(portStr));
				});
				rootAndFilter.Add(oidFilter);
			}

			return (rootAndFilter);
		}
	}

	private ClearRawDeviceFilter() {
		this._rawFilterTree = null;
	}

	public HasEqualFilterSettings(other: DeviceFilter): boolean {
		if (!other)
			return false;

		if (this.Name != other.Name)
			return false;

		if (this._baseIncludeScope != other._baseIncludeScope)
			return false;

		if (this._categoryFilterType != other._categoryFilterType)
			return false;

		// NAME Filter
		if (this._nameFilters.Count != other._nameFilters.Count)
			return false;

		this._nameFilters.ForEach((filter) => {
			if (!other._nameFilters.Contains(filter))
				return false;
		});

		// IP Scope Filter
		if (this._ipScopeFilters.Count != other._ipScopeFilters.Count)
			return false;

		this._ipScopeFilters.ForEach((filter) => {
			if (!other._ipScopeFilters.Contains(filter))
				return false;
		});

		// VLAN Filter
		if (this._vlanFilters.Count != other._vlanFilters.Count)
			return false;

		this._vlanFilters.ForEach((filter) => {
			if (!other._vlanFilters.Contains(filter))
				return false;
		});

		// OID Filter
		if (this._oidFilters.Count != other._oidFilters.Count)
			return false;

		this._oidFilters.ForEach((filter) => {
			if (!other._oidFilters.Contains(filter))
				return false;
		});

		// PORT Filter
		if (this._portFilters.Count != other._portFilters.Count)
			return false;

		this._portFilters.ForEach((filter) => {
			if (!other._portFilters.Contains(filter))
				return false;
		});

		// CATEGORY Filter
		if (this._categoryFilters.Count != other._categoryFilters.Count)
			return false;

		this._categoryFilters.ForEach((filter) => {
			if (!other._categoryFilters.Contains(filter))
				return false;
		});

		return true;
	}

	public Match(device: Device): boolean {
		if (!this._rawFilterTree) {
			this._rawFilterTree = this.BuildRawDeviceFilter();
		}

		return this._rawFilterTree.Match(device);
	}

	public Apply(devices: Iterable<Device>): HashSet<Device> {
		var result = new HashSet<Device>();

		devices.ForEach((dev) => {
			if (this.Match(dev))
				result.Add(dev);
		});

		return result;
	}

	public ApplyCallBack(devices: Iterable<Device>, onDevice: (device: Device, count: number) => void): number {
		var count = 0;

		devices.ForEach((dev) => {
			if (this.Match(dev)) {
				++count;
				if (onDevice)
					onDevice(dev, count);
			}
		});

		return count;
	}

	public get Summary(): string {
		let filterString = "";
		let selectString = "SELECT * FROM";
		let andString = "AND";
		let orString = "OR";
		let whereString = "WHERE";

		let hasWhereFilter = false;
		let nameFilter = "";
		let ipScopeFilter = "";
		let categoryFilter = "";
		let vlanFilter = "";
		let oidFilter = "";
		let portFilter = "";

		let incScope = "<All Devices>";
		switch(this.BaseIncludeScope) {
			case IncludeScope.AllNetworkDevices:
				incScope = "<Network Devices>";
				break;
			case IncludeScope.AllSnmpDevices:
				incScope = "<SNMP Devices>";
				break;
			case IncludeScope.NoDevices:
				return "SELECT <No Devices>";
		}

		filterString += `${selectString} ${incScope}`;

		if (this.HasNameFilters) {
			hasWhereFilter = true;
			this.NameFilters.ForEach((name: string) => {
				if (nameFilter)
					nameFilter += ", ";
				nameFilter += `\"${name}\"`;
			}, this);
		}

		if (this.HasIpScopeFilters) {
			hasWhereFilter = true;
			this.IpScopeFilters.ForEach((scope: string) => {
				if (ipScopeFilter)
					ipScopeFilter += ", ";
				ipScopeFilter += `\"${scope}\"`;
			}, this);
		}

		if (this.HasCategoryFilters) {
			hasWhereFilter = true;
			this.CategoryFilters.ForEach((category: string) => {
				if (categoryFilter)
					categoryFilter += ", ";
				categoryFilter += `\"${category}\"`;
			}, this);
		}

		if (this.HasVlanFilters) {
			hasWhereFilter = true;
			this.VlanFilters.ForEach((vlan: string) => {
				if (vlanFilter)
					vlanFilter += ", ";
				vlanFilter += `\"${vlan}\"`;
			}, this);
		}

		if (this.HasOidFilters) {
			hasWhereFilter = true;
			this.OidFilters.ForEach((oid: string) => {
				if (oidFilter)
					oidFilter += ", ";
				oidFilter += `\"${oid}\"`;
			}, this);
		}

		if (this.HasPortFilters) {
			hasWhereFilter = true;
			this.PortFilters.ForEach((port: string) => {
				if (portFilter)
					portFilter += ", ";
				portFilter += `\"${port}\"`;
			}, this);
		}


		if (hasWhereFilter) {
			let useAnd = false;

			filterString += "\n";
			filterString += `  ${whereString} \n`;

			if (nameFilter) {
				filterString += `     <NAME> IN ( ${nameFilter} )\n`;
				useAnd = true;
			}

			if (ipScopeFilter) {
				if (useAnd)
					filterString += `  ${andString}\n`;
				filterString += `     <IP ADDRESS> IN ( ${ipScopeFilter} )\n`;
				useAnd = true;
			}

			if (categoryFilter) {
				if (useAnd)
					filterString += `  ${andString}\n`;
				filterString += `     <CATEGORY> IN ( ${categoryFilter} )\n`;
				useAnd = true;
			}

			if (vlanFilter) {
				if (useAnd)
					filterString += `  ${andString}\n`;
				filterString += `     <VLAN> IN ( ${vlanFilter} )\n`;
				useAnd = true;
			}

			if (oidFilter) {
				if (useAnd)
					filterString += `  ${andString}\n`;
				filterString += `     <OID> IN ( ${oidFilter} )\n`;
				useAnd = true;
			}

			if (portFilter) {
				if (useAnd)
					filterString += `  ${andString}\n`;
				filterString += `     <PORT> IN ( ${portFilter} )\n`;
				useAnd = true;
			}
		}

		return filterString;
	}

	public static readonly ObjSerName = "DeviceFilter.ObjSerName";

	public Write(obj: any, context: any): void {

		let objSerName: string = "device_filter";
		if (context[DeviceFilter.ObjSerName]) {
			objSerName = context[DeviceFilter.ObjSerName];
		}

		obj[objSerName] = {};
		obj = obj[objSerName];

		super.Write(obj, context);

		this.WriteName(obj);
		this.WriteIncludeScope(obj);
		this.WriteCategoryFilterType(obj);

		this.WriteNameFilters(obj, context);
		this.WriteIpScopeFilters(obj, context);
		this.WriteCategoryFilters(obj, context);
		this.WriteVlanFilters(obj, context);
		this.WriteOidFilters(obj, context);
		this.WritePortFilters(obj, context);
		this.WriteCustomFilter(obj, context);
	}

	private WriteName(obj: any): void {
		if (this.HasName)
			obj.name = this.Name;
	}

	private WriteIncludeScope(obj: any): void {
		obj.include_scope = IncludeScope[this._baseIncludeScope];
	}

	private WriteCategoryFilterType(obj: any): void {
		obj.category_filter_type = CompositeFilterType[this._categoryFilterType];
	}

	private WriteNameFilters(obj: any, context: any): void {
		if (this.HasNameFilters) {
			obj.name_filter = [];
			ObjSerUtils.WriteStringEntryCollection(obj.name_filter, this.NameFilters, context);
		}
	}

	private WriteIpScopeFilters(obj: any, context: any): void {
		if (this.HasIpScopeFilters) {
			obj.ip_scope_filter = [];
			ObjSerUtils.WriteStringEntryCollection(obj.ip_scope_filter, this.IpScopeFilters, context);
		}
	}

	private WriteCategoryFilters(obj: any, context: any): void {
		if (this.HasCategoryFilters) {
			obj.category_filter = [];
			ObjSerUtils.WriteStringEntryCollection(obj.category_filter, this.CategoryFilters, context);
		}
	}

	private WriteVlanFilters(obj: any, context: any): void {
		if (this.HasVlanFilters) {
			obj.vlan_filter = [];
			ObjSerUtils.WriteStringEntryCollection(obj.vlan_filter, this.VlanFilters, context);
		}
	}

	private WriteOidFilters(obj: any, context: any): void {
		if (this.HasOidFilters) {
			obj.oid_filter = [];
			ObjSerUtils.WriteStringEntryCollection(obj.oid_filter, this.OidFilters, context);
		}
	}

	private WritePortFilters(obj: any, context: any): void {
		if (this.HasPortFilters) {
			obj.port_filter = [];
			ObjSerUtils.WriteStringEntryCollection(obj.port_filter, this.PortFilters, context);
		}
	}

	private WriteCustomFilter(obj: any, context: any): void {
		if (this.HasCustomFilter) {
			obj.custom_filter = {};
			obj = obj.custom_filter;

			this.CustomFilter.Write(obj, context);
		}
	}

	public Read(obj: any, context: any): void {
		this.Clear();

		let objSerName: string = "device_filter";
		if (context[DeviceFilter.ObjSerName]) {
			objSerName = context[DeviceFilter.ObjSerName];
		}

		if (!obj[objSerName])
			throw new Error();
		obj = obj[objSerName];

		super.Read(obj, context);

		if (obj.name !== undefined)
			this.Name = obj.name;

		if (obj.include_scope !== undefined)
			this.BaseIncludeScope = <IncludeScope>IncludeScope[<string>obj.include_scope];

		if (obj.category_filter_type !== undefined)
			this.CategoryFilterType = <CompositeFilterType>CompositeFilterType[<string>obj.category_filter_type];

		if (obj.name_filter !== undefined) {
			obj.name_filter.forEach((value: any) => {
				this.AddNameFilter(value.entry);
			}, this);
		}

		if (obj.ip_scope_filter !== undefined) {
			obj.ip_scope_filter.forEach((value: any) => {
				this.AddIpScopeFilter(value.entry);
			}, this);
		}

		if (obj.category_filter !== undefined) {
			obj.category_filter.forEach((value: any) => {
				this.AddCategoryFilterByName(value.entry);
			}, this);
		}

		if (obj.vlan_filter !== undefined) {
			obj.vlan_filter.forEach((value: any) => {
				this.AddVlanFilter(value.entry);
			}, this);
		}

		if (obj.oid_filter !== undefined) {
			obj.oid_filter.forEach((value: any) => {
				this.AddOidFilter(value.entry);
			}, this);
		}

		if (obj.port_filter !== undefined) {
			obj.port_filter.forEach((value: any) => {
				this.AddPortFilter(value.entry);
			}, this);
		}

		if (obj.custom_filter !== undefined) {
			this.CustomFilter = DeviceFilterUtils.Read(obj.custom_filter, context);
		}
	}
}

export interface IDeviceFilter extends ObjectSerializer {
	Match: (device: Device) => boolean;
}

export interface ICompositeDeviceFilter extends IDeviceFilter {
	Clear();
	Add(entry: IDeviceFilter);
}

export class CompositeDeviceFilterBase implements ICompositeDeviceFilter {
	protected Entries: List<IDeviceFilter>;
	private ObjSerName: string;

	constructor(objSerName: string) {
		this.Entries = new List<IDeviceFilter>();
		this.ObjSerName = objSerName;
	}

	public Match(device: Device): boolean {
		return false;
	}

	public Clear() {
		this.Entries.Clear();
	}

	public Add(entry: IDeviceFilter) {
		this.Entries.Add(entry);
	}

	public Write(obj: any, context: any): void {
		obj[this.ObjSerName] = {};
		obj = obj[this.ObjSerName];

		obj.entries = [];
		ObjSerUtils.WriteObjSerCollection(obj.entries, this.Entries, context);
	}

	public Read(obj: any, context: any): void {
		this.Clear();

		if (!obj[this.ObjSerName])
			throw new Error();
		obj = obj[this.ObjSerName];

		if (obj.entries !== undefined) {
			ObjSerUtils.ReadCollection(obj.entries, this.Entries, (value: any) => {
				return DeviceFilterUtils.Read(value, context);
			}, context);
		}
	}
}

export class AndFilter extends CompositeDeviceFilterBase {
	constructor() {
		super("and_filter");
	}

	public Match(device: Device): boolean {
		return this.Entries.Every((entry) => {
			return entry.Match(device);
		});
	}
}

export class OrFilter extends CompositeDeviceFilterBase {
	constructor() {
		super("or_filter");
	}

	public Match(device: Device): boolean {
		return this.Entries.Some((entry) => {
			return entry.Match(device);
		});
	}
}

export class NotFilter implements IDeviceFilter {
	private _sub: IDeviceFilter;
	public get Sub(): IDeviceFilter {
		return this._sub;
	}

	public set Sub(val: IDeviceFilter) {
		this._sub = val;
	}

	constructor(sub?: IDeviceFilter) {
		this._sub = sub;
	}

	public Match(device: Device): boolean {
		return !this._sub.Match(device);
	}

	public Write(obj: any, context: any): void {
		obj.not_filter = {};
		obj = obj.not_filter;

		if (this.Sub) {
			obj.sub_filter = {};
			this.Sub.Write(obj.sub_filter, context);
		}
	}

	public Read(obj: any, context: any): void {
		this.Sub = null;

		if (!obj.not_filter)
			throw new Error();
		obj = obj.not_filter;

		if (obj.sub_filter !== undefined)
			this.Sub = DeviceFilterUtils.Read(obj.sub_filter, context);
	}
}

export class IpScopeFilter implements IDeviceFilter {
	private _ipScope: IpScope;
	public get IpScope() {
		return this._ipScope;
	}
	public set IpScope(val: IpScope) {
		this._ipScope = val;
	}

	constructor(ipScope?: IpScope) {
		this._ipScope = ipScope;
	}

	public Match(device: Device): boolean {
		if (device.HasIpAddress) {
			if (this._ipScope.Contains(device.IpAddress))
				return true;
		}
		return device.IpAddresses.Some((ipAddr) => {
			return this.IpScope.Contains(ipAddr);
		});
	}

	public Write(obj: any, context: any): void {
		obj.ip_scope_filter = {};
		obj = obj.ip_scope_filter;

		if (this.IpScope)
			this.IpScope.Write(obj, context);
	}

	public Read(obj: any, context: any): void {
		this.IpScope = null;

		if (!obj.ip_scope_filter)
			throw new Error();
		obj = obj.ip_scope_filter;

		if (obj.ip_scope !== undefined) {
			this.IpScope = new IpScope();
			this.IpScope.Read(obj, context);
		}
	}
}

//export interface DeviceCategoryFilterJson {
//	device_category: string;
//}

export class DeviceCategoryFilter implements IDeviceFilter {
	public DeviceCategory: DeviceCategory;

	constructor(deviceCategory?: DeviceCategory) {
		this.DeviceCategory = deviceCategory;
	}

	public Match(device: Device): boolean {
		return device.Categories.Some((entry) => {
			return this.DeviceCategory.Equals(entry.Category);
		});
	}

	public Write(obj: any, context: any): void {
		obj.category_filter = {};
		obj = obj.category_filter;

		if (this.DeviceCategory)
			obj.device_category = this.DeviceCategory.Name;
	}

	public Read(obj: any, context: any): void {
		this.DeviceCategory = null;

		if (!obj.category_filter)
			throw new Error();
		obj = obj.category_filter;

		if (obj.device_category !== undefined)
			this.DeviceCategory = DeviceCategory.Get(obj.device_category);
	}
}

//export interface VlanFilterJson {
//	vlan_string: string;
//}

export class VlanFilter implements IDeviceFilter {
	public VlanString: string;

	constructor(vlanString?: string) {
		this.VlanString = vlanString;
	}

	public Match(device: Device): boolean {
		if (device.HasVlanTable) {
			var vlanIndex: number = parseInt(this.VlanString);
			var haveVlanIndex = !isNaN(vlanIndex);

			var regex = new RegExp(StringUtils.WildcardToRegex(this.VlanString), 'i');

			return device.VlanTable.Some((entry) => {
				if (entry.HasName) {
					if (regex.test(entry.Name))
						return true;
				}

				if (haveVlanIndex) {
					if (vlanIndex == entry.Index)
						return true;
				}
			});
		}

		return false;
	}

	public Write(obj: any, context: any): void {
		obj.vlan_filter = {};
		obj = obj.vlan_filter;

		if (this.VlanString)
			obj.vlan_string = this.VlanString;
	}

	public Read(obj: any, context: any): void {
		this.VlanString = "";

		if (!obj.vlan_filter)
			throw new Error();
		obj = obj.vlan_filter;

		if (obj.vlan_string !== undefined)
			this.VlanString = obj.vlan_string;
	}
}

export class DeviceNameFilter implements IDeviceFilter {
	private _DeviceName: string;
	private _Regex: RegExp;

	public get DeviceName(): string {
		return this._DeviceName;
	}

	public set DeviceName(value: string) {
		this._DeviceName = value;
		if (this._DeviceName)
			this._Regex = DeviceNameFilter.CreateRegex(this._DeviceName);
		else
			this._Regex = null;
	}

	public static CreateRegex(pattern: string): RegExp {
		return new RegExp(StringUtils.WildcardToRegex(pattern), "i");
	}

	constructor(deviceName?: string) {
		this.DeviceName = deviceName;
	}

	public Match(device: Device): boolean {
		if (this._Regex && device.HasSystemInfo) {
			var sysInfo: SystemInfo = device.SystemInfo;

			if (sysInfo.HasHostname) {
				if (this._Regex.test(sysInfo.Hostname))
					return true;
			}

			if (sysInfo.HasSystemName) {
				if (this._Regex.test(sysInfo.SystemName))
					return true;
			}

			if (sysInfo.HasNetBiosName) {
				if (this._Regex.test(sysInfo.NetBiosName))
					return true;
			}
		}

		return false;
	}

	public Write(obj: any, context: any): void {
		obj.name_filter = {};
		obj = obj.name_filter;

		if (this.DeviceName)
			obj.device_name = this.DeviceName;
	}

	public Read(obj: any, context: any): void {
		this.DeviceName = "";

		if (!obj.name_filter)
			throw new Error();
		obj = obj.name_filter;

		if (obj.device_name !== undefined)
			this.DeviceName = obj.device_name;
	}
}

export class OidFilter implements IDeviceFilter {

	private _OidString: string;
	public get OidString(): string {
		return this._OidString;
	}
	public set OidString(value: string) {
		this._OidString = value;
	}

	constructor(oidString?: string) {
		this.OidString = oidString;
	}

	public Match(device: Device): boolean {
		if (device.HasSystemInfo && device.SystemInfo.HasSystemOID) {
			var oid = device.SystemInfo.SystemOID;
			var regex = new RegExp(StringUtils.WildcardToRegex(this.OidString), "i");
			if (regex.test(oid))
				return true;
		}

		return false;
	}

	public Write(obj: any, context: any): void {
		obj.oid_filter = {};
		obj = obj.oid_filter;

		if (this.OidString)
			obj.oid_string = this.OidString;
	}

	public Read(obj: any, context: any): void {
		this.OidString = "";

		if (!obj.oid_filter)
			throw new Error();
		obj = obj.oid_filter;

		if (obj.oid_string !== undefined)
			this.OidString = obj.oid_string;
	}
}

export class PortFilter implements IDeviceFilter {

	private _port: string;
	public get Port(): string {
		return this._port;
	}
	public set Port(value: string) {
		this._port = value;
	}

	constructor(port?: string) {
		this.Port = port;
	}

	public Match(device: Device): boolean {

		return false;
	}

	public Write(obj: any, context: any): void {
		obj.port_filter = {};
		obj = obj.port_filter;

		if (this.Port)
			obj.port = this.Port;
	}

	public Read(obj: any, context: any): void {
		this.Port = "";

		if (!obj.port_filter)
			throw new Error();
		obj = obj.port_filter;

		if (obj.port !== undefined)
			this.Port = obj.port;
	}
}

export class DeviceFilterUtils {

	public static Read(obj: any, context: any): IDeviceFilter {

		let result: IDeviceFilter = null;

		if (obj.and_filter !== undefined) {
			result = new AndFilter();
			result.Read(obj.and_filter, context);
		}
		else if (obj.or_filter !== undefined) {
			result = new OrFilter();
			result.Read(obj.or_filter, context);
		}
		else if (obj.not_filter !== undefined) {
			result = new NotFilter();
			result.Read(obj.not_filter, context);
		}
		else if (obj.ip_scope_filter !== undefined) {
			result = new IpScopeFilter();
			result.Read(obj.ip_scope_filter, context);
		}
		else if (obj.name_filter !== undefined) {
			result = new DeviceNameFilter();
			result.Read(obj.name_filter, context);
		}
		else if (obj.vlan_filter !== undefined) {
			result = new VlanFilter();
			result.Read(obj.vlan_filter, context);
		}
		else if (obj.oid_filter !== undefined) {
			result = new OidFilter();
			result.Read(obj.oid_filter, context);
		}
		else if (obj.port_filter !== undefined) {
			result = new PortFilter();
			result.Read(obj.port_filter, context);
		}
		else if (obj.category_filter !== undefined) {
			result = new DeviceCategoryFilter();
			result.Read(obj.category_filter, context);
		}
		else {
			return null;
		}

		return result;
	}

}

